import { PanelProps } from '@blueprintjs/core';
import * as React from 'react';

import { addDataSource } from 'src/actions/data-sources/action-creators/addDataSource';
import { refreshDataSourceTables } from 'src/actions/data-sources/action-creators/refreshDataSourceTables';
import { dataSourcesStardogRequestDispatchers } from 'src/actions/data-sources/actionCreators';
import { SAMPLE_MUSIC_DATA_SOURCE_TYPE } from 'src/constants/dataSourceTypeDescription';
import { SAMPLE_MUSIC_DATA_SOURCE } from 'src/constants/sampleDataSource';
import {
  DataSourceSelectionPanel,
  DataSourceSelectionPanelProps,
} from 'src/containers/DataSourceCreation/DataSourceSelectionPanel/DataSourceSelectionPanel';
import { DataSourceTypeCards } from 'src/containers/DataSourceCreation/DataSourceTypeCards';
import { DataSourcePanelContainer } from 'src/containers/ProjectResourceDialog/DataSourcePanelContainer';
import {
  ProjectVirtualGraphCreationPanel,
  ProjectVirtualGraphCreationPanelProps,
} from 'src/containers/ProjectResourceDialog/ProjectVirtualGraphCreationPanel/ProjectVirtualGraphCreationPanel';
import { ProjectResourceDialogPanelProps } from 'src/containers/ProjectResourceDialog/types';
import { connectionSelector } from 'src/selectors/connection';
import { dataSourcesByTypeSelector } from 'src/selectors/dataSourcesByType';
import { isConnectingSelector } from 'src/selectors/isConnecting';
import { isRefreshingDataSourcesSelector } from 'src/selectors/isRefreshingDataSources';
import { isStardogFeatureSupportedSelector } from 'src/selectors/isStardogFeatureSupported';
import { useIsMounted } from 'src/utils/hooks/useIsMounted';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';

export type DataSourceTypeSelectionPanelProps = ProjectResourceDialogPanelProps;

export const DataSourceTypeSelectionPanel: React.VFC<
  PanelProps<DataSourceTypeSelectionPanelProps>
> = ({ closePanel, openPanel, setFooterProps }) => {
  const isMounted = useIsMounted();
  const dispatch = useReduxDispatch();

  const connection = useReduxSelector(connectionSelector);
  const isConnecting = useReduxSelector(isConnectingSelector);
  const isRefreshing = useReduxSelector(isRefreshingDataSourcesSelector);
  const dataSourcesByType = useReduxSelector(dataSourcesByTypeSelector);
  const isDataSourceDescriptionEndpointSupported = useReduxSelector(
    isStardogFeatureSupportedSelector(
      'isDataSourceDescriptionEndpointSupported',
      connection
    )
  );

  const [isSampleLoading, setIsSampleLoading] = React.useState(false);

  const handleSelectDataSourceType = React.useCallback(
    async (selectedDataSourceType: string) => {
      if (!selectedDataSourceType) {
        return;
      }

      if (selectedDataSourceType === SAMPLE_MUSIC_DATA_SOURCE_TYPE) {
        const existingDataSource = dataSourcesByType[
          SAMPLE_MUSIC_DATA_SOURCE.type
        ]?.find(({ id }) => id === SAMPLE_MUSIC_DATA_SOURCE.id);

        setIsSampleLoading(true);
        if (existingDataSource) {
          // mimic what DataSourceSelectionPanel does in handleSelectionOfDataSource
          await dispatch(refreshDataSourceTables(SAMPLE_MUSIC_DATA_SOURCE.id));
        } else {
          // addDataSource refreshes data source tables
          const didSucceed = await dispatch(
            addDataSource(SAMPLE_MUSIC_DATA_SOURCE)
          );
          if (!didSucceed) {
            setIsSampleLoading(false);
            return;
          }
        }
        setIsSampleLoading(false);

        const props: ProjectVirtualGraphCreationPanelProps = {
          dataSourceId: SAMPLE_MUSIC_DATA_SOURCE.id,
          setFooterProps,
        };
        openPanel({
          renderPanel: ProjectVirtualGraphCreationPanel,
          props,
        });
        return;
      }

      const dataSources = dataSourcesByType[selectedDataSourceType] || [];

      if (!isDataSourceDescriptionEndpointSupported) {
        if (!dataSources.length) {
          return;
        }
        if (dataSources.length === 1) {
          if (connection && !dataSources[0].options) {
            await dataSourcesStardogRequestDispatchers.requestDataSourceOptions(
              dataSources[0].id,
              connection
            );
          }
          const props: ProjectVirtualGraphCreationPanelProps = {
            dataSourceId: dataSources[0].id,
            setFooterProps,
          };
          openPanel({
            renderPanel: ProjectVirtualGraphCreationPanel,
            props,
          });
          return;
        }
      }

      if (connection) {
        dataSources.forEach((dataSource) => {
          if (!dataSource.options) {
            dataSourcesStardogRequestDispatchers.requestDataSourceOptions(
              dataSource.id,
              connection
            );
          }
        });
      }

      const props: DataSourceSelectionPanelProps = {
        setFooterProps,
        type: selectedDataSourceType,
      };
      openPanel({
        renderPanel: DataSourceSelectionPanel,
        props,
      });
    },
    [
      connection,
      dataSourcesByType,
      dispatch,
      isDataSourceDescriptionEndpointSupported,
      openPanel,
      setFooterProps,
    ]
  );

  React.useEffect(() => {
    if (isMounted.current) {
      setFooterProps({
        handleBackButtonClick: closePanel,
      });
    }
  }, [closePanel, isMounted, setFooterProps]);

  return (
    <DataSourcePanelContainer>
      <DataSourceTypeCards
        isLoading={isConnecting || isRefreshing}
        isSampleLoading={isSampleLoading}
        onSelectDataSourceType={handleSelectDataSourceType}
      />
    </DataSourcePanelContainer>
  );
};
